import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import TeamSelector from '../components/TeamSelector';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  ShoppingBagIcon,
  LinkIcon,
  CheckIcon,
  XMarkIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

const TeamRoster = () => {
  const { user, isCoach } = useAuth();
  
  const [confirmState, setConfirmState] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [roster, setRoster] = useState([]);
  const [stats, setStats] = useState({
    totalMembers: 0,
    roleBreakdown: {},
    recentJoins: 0
  });
  const [loading, setLoading] = useState(true);
  const [userTeamRelationship, setUserTeamRelationship] = useState(null);
  const [teamDetails, setTeamDetails] = useState(null);
  const [uniformLink, setUniformLink] = useState('');
  const [isEditingUniformLink, setIsEditingUniformLink] = useState(false);
  const [isSavingUniformLink, setIsSavingUniformLink] = useState(false);

  // Check if user can manage team members (edit/delete)
  const canManageTeamMembers = () => {
    if (!user || !selectedTeam || !userTeamRelationship) return false;
    
    // Global admins and org_admins can always manage
    if (['admin', 'org_admin'].includes(user.role)) return true;
    
    // Coaches and assistant coaches can manage team members
    if (['coach', 'assistant_coach'].includes(userTeamRelationship.relationshipType)) return true;
    
    return false;
  };

  // Fetch roster data
  const selectedTeamId = selectedTeam?.id || null;

  useEffect(() => {
    const fetchRosterData = async () => {
      if (!selectedTeamId) {
        setRoster([]);
        setStats({ totalMembers: 0, roleBreakdown: {}, recentJoins: 0 });
        setUserTeamRelationship(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const teamId = selectedTeamId;

        // Fetch user's team relationship
        const userTeamsResponse = await fetch('/api/teams/my-teams', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (userTeamsResponse.ok) {
          const userTeams = await userTeamsResponse.json();
          const currentTeamRelationship = userTeams.find(team => team.id === teamId);
          setUserTeamRelationship(currentTeamRelationship);
        }

        // Fetch team details (including uniform_link)
        const teamDetailsResponse = await fetch(`/api/teams/${teamId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (teamDetailsResponse.ok) {
          const teamData = await teamDetailsResponse.json();
          setTeamDetails(teamData);
          setUniformLink(teamData.uniformLink || '');
        }

        // Fetch roster
        const rosterResponse = await fetch(`/api/roster?teamId=${teamId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (rosterResponse.ok) {
          const rosterData = await rosterResponse.json();
          setRoster(rosterData);
          
          // Calculate stats from roster data
          const totalMembers = rosterData.length;
          const roleBreakdown = {
            player: 0,
            coach: 0,
            assistant_coach: 0,
            parent: 0
          };
          let recentJoins = 0;
          
          // Calculate recent joins (last 30 days)
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          rosterData.forEach(member => {
            // Count by role - use relationship_type from roster
            const role = member.role || 'player';
            if (roleBreakdown.hasOwnProperty(role)) {
              roleBreakdown[role] = (roleBreakdown[role] || 0) + 1;
            } else {
              // Handle any unexpected role types
              roleBreakdown[role] = (roleBreakdown[role] || 0) + 1;
            }
            
            // Count recent joins
            if (member.joinDate) {
              const joinDate = new Date(member.joinDate);
              if (joinDate >= thirtyDaysAgo) {
                recentJoins++;
              }
            }
          });
          
          setStats({
            totalMembers,
            roleBreakdown,
            recentJoins
          });
        }

      } catch (error) {
        console.error('Error fetching roster data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRosterData();
  }, [selectedTeamId]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showMemberDetails, setShowMemberDetails] = useState(null);
  const [memberFamily, setMemberFamily] = useState([]);
  const [isLoadingFamily, setIsLoadingFamily] = useState(false);
  const [showAddParentForm, setShowAddParentForm] = useState(false);
  const [newParentEmail, setNewParentEmail] = useState('');
  const [newParentRelationshipType, setNewParentRelationshipType] = useState('parent');
  const [isLinkingParent, setIsLinkingParent] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [positionFilter, setPositionFilter] = useState('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importTeamId, setImportTeamId] = useState(null);
  const [availableTeams, setAvailableTeams] = useState([]);

  const handleTeamChange = (team) => {
    setSelectedTeam(prev => {
      if ((prev?.id || null) === (team?.id || null)) {
        return prev;
      }
      return team || null;
    });
  };

  // Check if user can edit uniform link (coaches and admins)
  const canEditUniformLink = () => {
    if (!user || !selectedTeam || !userTeamRelationship) return false;
    
    // Global admins and org_admins can always edit
    if (['admin', 'org_admin'].includes(user.role)) return true;
    
    // Coaches and assistant coaches can edit
    if (['coach', 'assistant_coach'].includes(userTeamRelationship.relationshipType)) return true;
    
    return false;
  };

  // Handle saving uniform link
  const handleSaveUniformLink = async () => {
    if (!selectedTeam && !teamDetails) {
      toast.error('No team selected');
      return;
    }

    // Use teamDetails.id if available, otherwise fall back to selectedTeam.id
    const teamId = teamDetails?.id || selectedTeam?.id;
    if (!teamId) {
      toast.error('No team ID available');
      return;
    }

    setIsSavingUniformLink(true);
    try {
      console.log('Saving uniform link for team:', teamId, 'URL:', uniformLink, 'selectedTeam:', selectedTeam, 'teamDetails:', teamDetails);
      const response = await fetch(`/api/teams/${teamId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          uniformLink: uniformLink || null
        })
      });

      if (response.ok) {
        const updatedTeam = await response.json();
        setTeamDetails(updatedTeam);
        setUniformLink(updatedTeam.uniformLink || '');
        setIsEditingUniformLink(false);
        toast.success('Uniform link saved!');
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to update uniform link:', response.status, errorData);
        toast.error(`Failed to update uniform link: ${errorData.error || `Server returned ${response.status}`}`);
      }
    } catch (error) {
      console.error('Error saving uniform link:', error);
      toast.error(`Network error: ${error.message}`);
    } finally {
      setIsSavingUniformLink(false);
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    role: 'player',
    position: '',
    jerseyNumber: '',
    height: '',
    weight: '',
    email: '',
    phone: '',
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    notes: '',
    uniform_info: {
      jersey_number: '',
      sizing_type: '',
      hat_size: '',
      shirt_size: '',
      pant_size: ''
    }
  });

  const handleInputChange = (nameOrEvent, valueOrUndefined) => {
    // Handle both event objects and direct name/value calls
    let name, value;
    
    if (typeof nameOrEvent === 'string') {
      // Direct call with name and value parameters
      name = nameOrEvent;
      value = valueOrUndefined;
    } else if (nameOrEvent && nameOrEvent.target) {
      // Event object
      name = nameOrEvent.target.name;
      value = nameOrEvent.target.value;
    } else {
      console.error('Invalid handleInputChange call:', nameOrEvent, valueOrUndefined);
      return;
    }
    
    // Handle nested objects like uniform_info
    if (name === 'uniform_info') {
      // Direct uniform_info object update
      setFormData(prev => ({
        ...prev,
        uniform_info: value
      }));
    } else if (name.startsWith('uniform_info.')) {
      // Nested uniform_info field update
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        uniform_info: {
          ...prev.uniform_info,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) {
      console.log('Already submitting, ignoring duplicate request');
      return;
    }
    
    if (!selectedTeam) {
      console.error('No team selected');
      toast.error('Please select a team first');
      return;
    }

    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }
    
    setIsSubmitting(true);
    
    const requestData = {
      teamId: selectedTeam.id,
      name: formData.name,
      email: formData.email,
      role: formData.role,
      phone: formData.phone,
      position: formData.position,
      jerseyNumber: formData.jerseyNumber,
      uniform_info: formData.uniform_info,
      notes: formData.notes
    };
    
    console.log('Submitting player data:', requestData);
    
    try {
      const response = await fetch('/api/roster', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestData)
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const newMember = await response.json();
        console.log('Successfully added member:', newMember);
        setRoster(prev => [newMember, ...prev]);
        setFormData({
          name: '',
          role: 'player',
          position: '',
          jerseyNumber: '',
          height: '',
          weight: '',
          email: '',
          phone: '',
          parentName: '',
          parentEmail: '',
          parentPhone: '',
          notes: '',
          uniform_info: {
            jersey_number: '',
            sizing_type: '',
            hat_size: '',
            shirt_size: '',
            pant_size: ''
          }
        });
        setShowAddForm(false);
        toast.success('Player added successfully!');
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        toast.error(`Failed to add member: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error(`Network error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditMember = async (memberId, updatedData) => {
    if (!selectedTeam) {
      console.error('No team selected for edit');
      return;
    }
    
    const requestData = {
      teamId: selectedTeam.id,
      ...updatedData
    };
    
    console.log('Updating member:', memberId, requestData);
    
    try {
      const response = await fetch(`/api/roster/${memberId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestData)
      });

      console.log('Edit response status:', response.status);
      
      if (response.ok) {
        const updatedMember = await response.json();
        console.log('Successfully updated member:', updatedMember);
        setRoster(prev => prev.map(member => 
          member.id === memberId ? updatedMember : member
        ));
        setEditingMember(null);
      } else {
        const errorData = await response.json();
        console.error('Edit error response:', errorData);
        toast.error(`Failed to update member: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error(`Network error: ${error.message}`);
    }
  };

  const startEditMember = (member) => {
    setEditingMember(member.id);
    setFormData({
      name: member.name || '',
      role: member.role || 'player',
      position: member.position || '',
      jerseyNumber: member.jersey_number || '',
      height: member.height || '',
      weight: member.weight || '',
      email: member.email || '',
      phone: member.phone || '',
      parentName: member.parentName || '',
      parentEmail: member.parentEmail || '',
      parentPhone: member.parentPhone || '',
      notes: member.notes || '',
      uniform_info: {
        jersey_number: member.uniform_info?.jersey_number || '',
        sizing_type: member.uniform_info?.sizing_type || '',
        hat_size: member.uniform_info?.hat_size || '',
        shirt_size: member.uniform_info?.shirt_size || '',
        pant_size: member.uniform_info?.pant_size || ''
      }
    });
  };

  const cancelEdit = () => {
    setEditingMember(null);
    setFormData({
      name: '',
      role: 'player',
      position: '',
      jerseyNumber: '',
      height: '',
      weight: '',
      email: '',
      phone: '',
      parentName: '',
      parentEmail: '',
      parentPhone: '',
      notes: '',
      uniform_info: {
        jersey_number: '',
        sizing_type: '',
        hat_size: '',
        shirt_size: '',
        pant_size: ''
      }
    });
  };


  const handleResendInvite = async (memberId) => {
    if (!selectedTeam) {
      console.error('No team selected for resend invite');
      return;
    }
    
    try {
      const response = await fetch(`/api/roster/${memberId}/resend-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ teamId: selectedTeam.id })
      });

      console.log('Resend invite response status:', response.status);
      
      if (response.ok) {
        toast.success('Invitation sent successfully!');
      } else {
        const errorData = await response.json();
        console.error('Resend invite error response:', errorData);
        toast.error(`Failed to resend invitation: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.error(`Network error: ${error.message}`);
    }
  };

  const handleDeleteMember = (memberId) => {
    if (!selectedTeam) {
      console.error('No team selected for delete');
      return;
    }

    setConfirmState({
      title: 'Remove Team Member',
      message: 'Are you sure you want to remove this team member?',
      isDangerous: true,
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/roster/${memberId}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({ teamId: selectedTeam.id }),
          });

          if (response.ok) {
            setRoster(prev => prev.filter(member => member.id !== memberId));
          } else {
            const errorData = await response.json();
            toast.error(`Failed to delete member: ${errorData.error || 'Unknown error'}`);
          }
        } catch (error) {
          console.error('Error deleting member:', error);
          toast.error(`Network error: ${error.message}`);
        }
      },
    });
  };

  const fetchMemberFamily = async (playerId) => {
    setIsLoadingFamily(true);
    try {
      const response = await fetch(`/api/parent-player/player/${playerId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMemberFamily(data.parents || []);
      } else {
        setMemberFamily([]);
      }
    } catch (error) {
      console.error('Error fetching player family:', error);
      setMemberFamily([]);
    } finally {
      setIsLoadingFamily(false);
    }
  };

  const closeMemberDetails = () => {
    setShowMemberDetails(null);
    setMemberFamily([]);
    setShowAddParentForm(false);
    setNewParentEmail('');
    setNewParentRelationshipType('parent');
  };

  const handleLinkParent = async (e) => {
    e.preventDefault();
    if (!showMemberDetails) return;

    setIsLinkingParent(true);
    try {
      const response = await fetch(`/api/parent-player/player/${showMemberDetails.id}/link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          parentEmail: newParentEmail,
          relationshipType: newParentRelationshipType,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMemberFamily(prev => [...prev, data.parent]);
        setNewParentEmail('');
        setNewParentRelationshipType('parent');
        setShowAddParentForm(false);
        toast.success('Parent linked successfully');
      } else {
        toast.error(data.error || 'Failed to link parent');
      }
    } catch (error) {
      console.error('Error linking parent:', error);
      toast.error(`Network error: ${error.message}`);
    } finally {
      setIsLinkingParent(false);
    }
  };

  const handleUnlinkParent = (relationshipId, parentName) => {
    setConfirmState({
      title: 'Remove Parent/Guardian',
      message: `Remove ${parentName} from this player's family?`,
      isDangerous: true,
      onConfirm: async () => {
        try {
          const response = await fetch(`/api/parent-player/relationship/${relationshipId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          });

          if (response.ok) {
            setMemberFamily(prev => prev.filter(p => p.relationshipId !== relationshipId));
          } else {
            const errorData = await response.json();
            toast.error(errorData.error || 'Failed to remove parent');
          }
        } catch (error) {
          console.error('Error removing parent:', error);
          toast.error(`Network error: ${error.message}`);
        }
      },
    });
  };

  useEffect(() => {
    if (showMemberDetails && showMemberDetails.role === 'player') {
      fetchMemberFamily(showMemberDetails.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMemberDetails?.id]);

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch('/api/roster/template', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'player_import_template.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const errorData = await response.json();
        toast.error(`Failed to download template: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error(`Network error: ${error.message}`);
    }
  };

  const fetchAvailableTeams = async () => {
    try {
      const response = await fetch('/api/roster/teams', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const teams = await response.json();
        setAvailableTeams(teams);
        // Set default team if none selected
        if (!importTeamId && teams.length > 0) {
          setImportTeamId(teams[0].id);
        }
      } else {
        console.error('Failed to fetch available teams');
      }
    } catch (error) {
      console.error('Error fetching available teams:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setImportFile(file);
      } else {
        toast.error('Please select a CSV file');
        e.target.value = '';
      }
    }
  };

  const handleImportPlayers = async (e) => {
    e.preventDefault();
    
    if (!importTeamId) {
      toast.error('Please select a team first');
      return;
    }

    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setIsImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('csvFile', importFile);
      formData.append('teamId', importTeamId);
      
      const response = await fetch('/api/roster/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (response.ok) {
        setImportResults(result);
        // Refresh the roster if we're viewing the same team
        if (selectedTeam && selectedTeam.id == importTeamId) {
          const rosterResponse = await fetch(`/api/roster?teamId=${importTeamId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (rosterResponse.ok) {
            const rosterData = await rosterResponse.json();
            setRoster(rosterData);
          }
        }
        
        setImportFile(null);
        setImportTeamId(null);
        setShowImportForm(false);
      } else {
        toast.error(`Import failed: ${result.error || 'Unknown error'}`);
        if (result.details) {
          console.error('Import errors:', result.details);
        }
      }
    } catch (error) {
      console.error('Error importing players:', error);
      toast.error(`Network error: ${error.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const filteredMembers = roster.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (member.position && member.position.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesRole = roleFilter === 'all' || member.role === roleFilter;
    const matchesPosition = positionFilter === 'all' || (member.position && member.position.toLowerCase().includes(positionFilter.toLowerCase()));
    return matchesSearch && matchesRole && matchesPosition;
  });

  const getRoleColor = (role) => {
    const colors = {
      player: 'bg-blue-100 text-blue-800',
      parent: 'bg-green-100 text-green-800',
      coach: 'bg-purple-100 text-purple-800',
      assistant_coach: 'bg-indigo-100 text-indigo-800',
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const canUseDirectContactActions = () => {
    if (!user) return false;
    if (['admin', 'org_admin'].includes(user.role)) return true;
    return canManageTeamMembers();
  };

  const normalizePhoneForLink = (phoneValue) => {
    if (!phoneValue) return '';
    const trimmed = String(phoneValue).trim();
    const hasPlusPrefix = trimmed.startsWith('+');
    const digitsOnly = trimmed.replace(/\D/g, '');
    if (!digitsOnly) return '';
    return hasPlusPrefix ? `+${digitsOnly}` : digitsOnly;
  };

  const getEmailHref = (emailValue) => {
    if (!emailValue) return '';
    return `mailto:${String(emailValue).trim()}`;
  };

  const getTelHref = (phoneValue) => {
    const normalizedPhone = normalizePhoneForLink(phoneValue);
    return normalizedPhone ? `tel:${normalizedPhone}` : '';
  };

  const getSmsHref = (phoneValue) => {
    const normalizedPhone = normalizePhoneForLink(phoneValue);
    return normalizedPhone ? `sms:${normalizedPhone}` : '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // No team selected state
  if (!selectedTeam) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Team Roster</h1>
          <p className="text-gray-600">Select a team to view and manage its roster.</p>
          <div className="mt-4">
            <TeamSelector 
              onTeamChange={handleTeamChange}
              className="w-full sm:w-64"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <UserGroupIcon className="h-8 w-8 text-blue-600 mr-3" />
              Team Roster
            </h1>
            <p className="text-gray-600 mt-1">
              {selectedTeam ? selectedTeam.name : 'All Teams'} • {stats.totalMembers} members
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 gap-3">
            <TeamSelector 
              onTeamChange={handleTeamChange}
              className="w-full sm:w-60"
            />
            {canManageTeamMembers() && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowAddForm(true)}
                  className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center text-sm sm:text-base sm:px-4"
                >
                  <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Add Member</span>
                  <span className="sm:hidden">Add</span>
                </button>
                <button
                  onClick={() => {
                    fetchAvailableTeams();
                    setShowImportForm(true);
                  }}
                  className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center text-sm sm:text-base sm:px-4"
                >
                  <DocumentArrowUpIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Import CSV</span>
                  <span className="sm:hidden">Import</span>
                </button>
                <button
                  onClick={handleDownloadTemplate}
                  className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 flex items-center justify-center text-sm sm:text-base sm:px-4"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Template</span>
                  <span className="sm:hidden">Template</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Members</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalMembers}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-bold">P</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Players</p>
              <p className="text-2xl font-bold text-gray-900">{stats.roleBreakdown.player || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 font-bold">C</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Coaches</p>
              <p className="text-2xl font-bold text-gray-900">{(stats.roleBreakdown.coach || 0) + (stats.roleBreakdown.assistant_coach || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <CalendarIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Recent Joins</p>
              <p className="text-2xl font-bold text-gray-900">{stats.recentJoins}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Uniform Link Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            {canEditUniformLink() ? (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Uniform Store Link
                </label>
                {isEditingUniformLink ? (
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={uniformLink}
                      onChange={(e) => setUniformLink(e.target.value)}
                      placeholder="https://example.com/uniforms"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={handleSaveUniformLink}
                      disabled={isSavingUniformLink}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                    >
                      <CheckIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingUniformLink(false);
                        setUniformLink(teamDetails?.uniformLink || '');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 flex items-center gap-2">
                      <LinkIcon className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {uniformLink ? (
                          <a
                            href={uniformLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline truncate"
                          >
                            {uniformLink}
                          </a>
                        ) : (
                          <span className="text-gray-400 italic">No uniform link set</span>
                        )}
                      </span>
                    </div>
                    <button
                      onClick={() => setIsEditingUniformLink(true)}
                      className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <PencilIcon className="h-4 w-4" />
                      {uniformLink ? 'Edit' : 'Add Link'}
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Add a link to your team's uniform storefront (e.g., EvoShield, Rawlings)
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShoppingBagIcon className="h-6 w-6 text-gray-400" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Order Uniforms</h3>
                    <p className="text-xs text-gray-500">
                      {uniformLink ? 'Click below to order team uniforms' : 'Uniform link not yet set by coach'}
                    </p>
                  </div>
                </div>
                {uniformLink && (
                  <a
                    href={uniformLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <ShoppingBagIcon className="h-5 w-5" />
                    Order Uniforms
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Roles</option>
            <option value="player">Players</option>
            <option value="coach">Coaches</option>
            <option value="assistant_coach">Assistant Coaches</option>
            <option value="parent">Parents</option>
          </select>
          <select
            value={positionFilter}
            onChange={(e) => setPositionFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Positions</option>
            <option value="pitcher">Pitcher</option>
            <option value="catcher">Catcher</option>
            <option value="first base">First Base</option>
            <option value="second base">Second Base</option>
            <option value="shortstop">Shortstop</option>
            <option value="third base">Third Base</option>
            <option value="outfield">Outfield</option>
          </select>
        </div>
      </div>

      {/* Roster List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {filteredMembers.length} {filteredMembers.length === 1 ? 'Member' : 'Members'}
          </h2>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredMembers.map((member) => (
            <div key={member.id} className="px-4 sm:px-6 py-4 hover:bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start space-x-3 sm:space-x-4 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gray-200 rounded-full flex items-center justify-center">
                      <span className="text-base sm:text-lg font-medium text-gray-600">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">
                        {member.name}
                      </h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getRoleColor(member.role)}`}>
                        {member.role.replace('_', ' ')}
                      </span>
                      {member.isPending && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 flex-shrink-0">
                          Pending
                        </span>
                      )}
                      {member.isPrimary && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0">
                          Primary
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-500">
                      {member.position && (
                        <span className="flex-shrink-0">{member.position}</span>
                      )}
                      {member.jerseyNumber && (
                        <span className="flex-shrink-0">#{member.jerseyNumber}</span>
                      )}
                      <span className="flex items-center min-w-0">
                        <EnvelopeIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                        {canUseDirectContactActions() ? (
                          <a
                            href={getEmailHref(member.email)}
                            className="truncate text-blue-600 hover:text-blue-800 hover:underline"
                            title={`Email ${member.name}`}
                          >
                            {member.email}
                          </a>
                        ) : (
                          <span className="truncate">{member.email}</span>
                        )}
                      </span>
                      {member.phone && (
                        <span className="flex items-center flex-wrap gap-2 flex-shrink-0">
                          <PhoneIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          <span className="whitespace-nowrap">{member.phone}</span>
                          {canUseDirectContactActions() && getTelHref(member.phone) && (
                            <a
                              href={getTelHref(member.phone)}
                              className="text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                              title={`Call ${member.name}`}
                            >
                              Call
                            </a>
                          )}
                          {canUseDirectContactActions() && getSmsHref(member.phone) && (
                            <a
                              href={getSmsHref(member.phone)}
                              className="text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                              title={`Text ${member.name}`}
                            >
                              Text
                            </a>
                          )}
                        </span>
                      )}
                    </div>
                    {member.notes && (
                      <p className="mt-1 text-xs sm:text-sm text-gray-600 line-clamp-2">{member.notes}</p>
                    )}
                    {member.role === 'player' && member.uniform_info && (
                      <div className="mt-2 text-xs sm:text-sm text-gray-500">
                        <span className="font-medium">Uniform: </span>
                        {member.uniform_info.shirt_size && `Shirt: ${member.uniform_info.shirt_size}`}
                        {member.uniform_info.shirt_size && member.uniform_info.pant_size && ' • '}
                        {member.uniform_info.pant_size && `Pants: ${member.uniform_info.pant_size}`}
                        {member.uniform_info.hat_size && ` • Hat: ${member.uniform_info.hat_size}`}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-2 flex-shrink-0">
                  <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                    Joined {formatDate(member.joinDate)}
                  </span>
                  {canManageTeamMembers() && (
                    <div className="flex items-center space-x-1">
                      {member.isPending && (
                        <button
                          onClick={() => handleResendInvite(member.id)}
                          className="p-1 text-orange-500 hover:text-orange-700"
                          aria-label="Re-send invitation"
                          title="Re-send invitation email"
                        >
                          <PaperAirplaneIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                      )}
                      <button
                        onClick={() => setShowMemberDetails(member)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        aria-label="View details"
                      >
                        <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                      <button
                        onClick={() => startEditMember(member)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        aria-label="Edit member"
                      >
                        <PencilIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteMember(member.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        aria-label="Delete member"
                      >
                        <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Team Member</h3>
              <form onSubmit={handleAddMember} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="player">Player</option>
                    <option value="coach">Coach</option>
                    <option value="assistant_coach">Assistant Coach</option>
                    <option value="parent">Parent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Position</label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Jersey Number</label>
                  <input
                    type="text"
                    name="jerseyNumber"
                    value={formData.jerseyNumber}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                {/* Uniform Information - Only show for players */}
                {formData.role === 'player' && (
                  <>
                    <div className="col-span-2">
                      <h4 className="text-md font-medium text-gray-900 mb-2">Uniform Information</h4>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Jersey Number</label>
                      <input
                        type="number"
                        name="uniform_info.jersey_number"
                        value={formData.uniform_info.jersey_number}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Sizing Type</label>
                      <select
                        name="uniform_info.sizing_type"
                        value={formData.uniform_info.sizing_type}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select sizing type</option>
                        <option value="Adult">Adult</option>
                        <option value="Child">Child</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Hat Size</label>
                      <input
                        type="text"
                        name="uniform_info.hat_size"
                        value={formData.uniform_info.hat_size}
                        onChange={handleInputChange}
                        placeholder="e.g., 7 1/4, Large"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Shirt Size</label>
                      <select
                        name="uniform_info.shirt_size"
                        value={formData.uniform_info.shirt_size}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select shirt size</option>
                        <option value="SM">SM</option>
                        <option value="MD">MD</option>
                        <option value="L">L</option>
                        <option value="XL">XL</option>
                        <option value="XXL">XXL</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Pant Size</label>
                      <select
                        name="uniform_info.pant_size"
                        value={formData.uniform_info.pant_size}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select pant size</option>
                        <option value="SM">SM</option>
                        <option value="MD">MD</option>
                        <option value="L">L</option>
                        <option value="XL">XL</option>
                        <option value="XXL">XXL</option>
                      </select>
                    </div>
                  </>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isSubmitting ? 'Adding...' : 'Add Member'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Team Member</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                handleEditMember(editingMember, formData);
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="player">Player</option>
                    <option value="coach">Coach</option>
                    <option value="assistant_coach">Assistant Coach</option>
                    <option value="parent">Parent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Position</label>
                  <input
                    type="text"
                    value={formData.position}
                    onChange={(e) => handleInputChange('position', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Jersey Number</label>
                  <input
                    type="text"
                    value={formData.jerseyNumber}
                    onChange={(e) => handleInputChange('jerseyNumber', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Uniform Information */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Uniform Information</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Jersey Size</label>
                      <input
                        type="number"
                        value={formData.uniform_info.jersey_number || ''}
                        onChange={(e) => handleInputChange('uniform_info', { ...formData.uniform_info, jersey_number: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Sizing Type</label>
                      <select
                        value={formData.uniform_info.sizing_type || ''}
                        onChange={(e) => handleInputChange('uniform_info', { ...formData.uniform_info, sizing_type: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select...</option>
                        <option value="Adult">Adult</option>
                        <option value="Child">Child</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Hat Size</label>
                      <input
                        type="text"
                        value={formData.uniform_info.hat_size || ''}
                        onChange={(e) => handleInputChange('uniform_info', { ...formData.uniform_info, hat_size: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Shirt Size</label>
                      <select
                        value={formData.uniform_info.shirt_size || ''}
                        onChange={(e) => handleInputChange('uniform_info', { ...formData.uniform_info, shirt_size: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select...</option>
                        <option value="XS">XS</option>
                        <option value="S">S</option>
                        <option value="M">M</option>
                        <option value="L">L</option>
                        <option value="XL">XL</option>
                        <option value="XXL">XXL</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Pant Size</label>
                      <select
                        value={formData.uniform_info.pant_size || ''}
                        onChange={(e) => handleInputChange('uniform_info', { ...formData.uniform_info, pant_size: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select...</option>
                        <option value="XS">XS</option>
                        <option value="S">S</option>
                        <option value="M">M</option>
                        <option value="L">L</option>
                        <option value="XL">XL</option>
                        <option value="XXL">XXL</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isSubmitting ? 'Updating...' : 'Update Member'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Import Players from CSV</h3>
              <form onSubmit={handleImportPlayers} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Team
                  </label>
                  <select
                    value={importTeamId || ''}
                    onChange={(e) => setImportTeamId(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Choose a team...</option>
                    {availableTeams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select CSV File
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Download the template to see the required format.
                  </p>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowImportForm(false);
                      setImportFile(null);
                      setImportTeamId(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isImporting || !importFile || !importTeamId}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      isImporting || !importFile || !importTeamId
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {isImporting ? 'Importing...' : 'Import Players'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Import Results Modal */}
      {importResults && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Import Results</h3>
              <div className="space-y-4">
                <div className="bg-green-50 p-3 rounded-md">
                  <p className="text-sm text-green-800">
                    <strong>Success:</strong> {importResults.summary.successful} players processed
                  </p>
                </div>
                
                {importResults.summary.failed > 0 && (
                  <div className="bg-red-50 p-3 rounded-md">
                    <p className="text-sm text-red-800">
                      <strong>Failed:</strong> {importResults.summary.failed} players
                    </p>
                  </div>
                )}
                
                {importResults.created.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Successfully Added:</h4>
                    <div className="max-h-32 overflow-y-auto">
                      {importResults.created.map((player, index) => (
                        <div key={index} className="text-sm text-gray-600 mb-1">
                          {player.name} ({player.email}) - {player.status}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {importResults.failed.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Failed:</h4>
                    <div className="max-h-32 overflow-y-auto">
                      {importResults.failed.map((player, index) => (
                        <div key={index} className="text-sm text-red-600 mb-1">
                          {player.name} ({player.email}) - {player.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setImportResults(null)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Member Details Modal */}
      {showMemberDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Member Details</h3>
              <button
                onClick={closeMemberDetails}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-xl font-medium text-gray-600">
                    {showMemberDetails.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h4 className="text-xl font-medium text-gray-900">{showMemberDetails.name}</h4>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${getRoleColor(showMemberDetails.role)}`}>
                    {showMemberDetails.role.replace('_', ' ')}
                  </span>
                </div>
              </div>

              {showMemberDetails.role === 'player' && (
                <div className="grid grid-cols-2 gap-4">
                  {showMemberDetails.position && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Position:</span>
                      <p className="text-sm text-gray-900">{showMemberDetails.position}</p>
                    </div>
                  )}
                  {showMemberDetails.jerseyNumber && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Jersey Number:</span>
                      <p className="text-sm text-gray-900">#{showMemberDetails.jerseyNumber}</p>
                    </div>
                  )}
                  {showMemberDetails.height && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Height:</span>
                      <p className="text-sm text-gray-900">{showMemberDetails.height}</p>
                    </div>
                  )}
                  {showMemberDetails.weight && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Weight:</span>
                      <p className="text-sm text-gray-900">{showMemberDetails.weight}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-gray-200 pt-4">
                <h5 className="text-sm font-medium text-gray-900 mb-2">Contact Information</h5>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900">{showMemberDetails.email}</span>
                  </div>
                  {showMemberDetails.phone && (
                    <div className="flex items-center space-x-2">
                      <PhoneIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-900">{showMemberDetails.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {showMemberDetails.role === 'player' && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-sm font-medium text-gray-900">Parents / Guardians</h5>
                    {!showAddParentForm && (
                      <button
                        type="button"
                        onClick={() => setShowAddParentForm(true)}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800"
                      >
                        + Add Parent
                      </button>
                    )}
                  </div>

                  {isLoadingFamily ? (
                    <p className="text-sm text-gray-500">Loading family...</p>
                  ) : (
                    <div className="space-y-2">
                      {memberFamily.length === 0 && !showAddParentForm && (
                        <p className="text-sm text-gray-500">No parents or guardians linked yet.</p>
                      )}
                      {memberFamily.map((parent) => (
                        <div
                          key={parent.relationshipId}
                          className="flex items-center justify-between text-sm bg-gray-50 rounded-md px-3 py-2"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 truncate">{parent.name}</span>
                              <span className="text-xs text-gray-500 capitalize">
                                {parent.relationshipType?.replace('_', ' ')}
                              </span>
                              {parent.isPrimary && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Primary
                                </span>
                              )}
                            </div>
                            <div className="text-gray-500 truncate">{parent.email}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleUnlinkParent(parent.relationshipId, parent.name)}
                            className="ml-2 flex-shrink-0 text-gray-400 hover:text-red-600"
                            aria-label={`Remove ${parent.name}`}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {showAddParentForm && (
                    <form onSubmit={handleLinkParent} className="mt-3 space-y-2 bg-gray-50 rounded-md p-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Parent's Email
                        </label>
                        <input
                          type="email"
                          value={newParentEmail}
                          onChange={(e) => setNewParentEmail(e.target.value)}
                          placeholder="parent@example.com"
                          required
                          className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          The parent must already have an account with this email.
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Relationship
                        </label>
                        <select
                          value={newParentRelationshipType}
                          onChange={(e) => setNewParentRelationshipType(e.target.value)}
                          className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="parent">Parent</option>
                          <option value="guardian">Guardian</option>
                          <option value="emergency_contact">Emergency Contact</option>
                        </select>
                      </div>
                      <div className="flex justify-end space-x-2 pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddParentForm(false);
                            setNewParentEmail('');
                            setNewParentRelationshipType('parent');
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isLinkingParent}
                          className={`px-3 py-1.5 text-xs font-medium text-white rounded-md ${
                            isLinkingParent ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                        >
                          {isLinkingParent ? 'Linking...' : 'Link Parent'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {showMemberDetails.role === 'player' && showMemberDetails.uniform_info && (
                <div className="border-t border-gray-200 pt-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Uniform Info</h5>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-900">
                    {showMemberDetails.uniform_info.shirt_size && (
                      <span>Shirt: {showMemberDetails.uniform_info.shirt_size}</span>
                    )}
                    {showMemberDetails.uniform_info.pant_size && (
                      <span>Pants: {showMemberDetails.uniform_info.pant_size}</span>
                    )}
                    {showMemberDetails.uniform_info.hat_size && (
                      <span>Hat: {showMemberDetails.uniform_info.hat_size}</span>
                    )}
                  </div>
                </div>
              )}

              {showMemberDetails.notes && (
                <div className="border-t border-gray-200 pt-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Notes</h5>
                  <p className="text-sm text-gray-900">{showMemberDetails.notes}</p>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4 flex items-center space-x-2">
                <CalendarIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-500">Member since:</span>
                <span className="text-sm text-gray-900">{formatDate(showMemberDetails.joinDate)}</span>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={closeMemberDetails}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        isDangerous={confirmState?.isDangerous}
        onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
};

export default TeamRoster;
